// ═══════════════════════════════════════════════════════════
//  LaunchpadBubble.jsx  —  Floating huddle bubble for React
//  Drop this in: src/components/LaunchpadBubble.jsx
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { useLaunchpad } from '../hooks/useLaunchpad';
import { broadcastLaunchpadInvite } from '../db';

// ── 40Geo palette ─────────────────────────────────────────
const ACCENT = '#E87722'; // orange
const NAVY   = '#0D1B2A'; // dark navy bg

// ── Tiny colour helper (same logic as hook) ────────────────
const colorFromId = (id = '') => {
  const colors = ['#E87722','#FF3CAC','#7B2FFF','#FFB830','#FF6B6B','#3CF0FF'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
};

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

// ── Video tile sub-component ───────────────────────────────
function VideoTile({ stream, name, initials, color, speaking, muted = false, size = 'md' }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const dim = size === 'lg' ? 200 : size === 'sm' ? 56 : 80;

  return (
    <div style={{
      position: 'relative',
      width: dim,
      height: dim,
      borderRadius: size === 'sm' ? '50%' : 12,
      overflow: 'hidden',
      border: speaking ? `2px solid ${color}` : 'rgba(232,119,34,0.12)',
      boxShadow: speaking ? `0 0 16px ${color}55` : 'none',
      flexShrink: 0,
      background: NAVY,
      transition: 'border-color 0.2s, box-shadow 0.2s',
    }}>
      {stream && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={muted}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}

      {!stream && (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: `radial-gradient(circle, ${color}22 0%, transparent 70%)`,
          fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
          fontSize: size === 'sm' ? 14 : 20,
          color,
          letterSpacing: '0.05em',
        }}>
          {initials || '?'}
        </div>
      )}

      {size !== 'sm' && (
        <div style={{
          position: 'absolute', bottom: 6, left: 6,
          background: 'rgba(13,27,42,0.85)',
          border: '1px solid rgba(232,119,34,0.15)',
          borderRadius: 4, padding: '2px 6px',
          fontSize: 10, color: 'rgba(255,255,255,0.75)',
          letterSpacing: '0.06em',
          fontFamily: "'Montserrat',sans-serif",
          backdropFilter: 'blur(6px)',
        }}>
          {name}
        </div>
      )}

      {speaking && (
        <div style={{
          position: 'absolute', inset: -3,
          borderRadius: 'inherit',
          border: `2px solid ${color}`,
          animation: 'lp-ring 1.5s ease-in-out infinite',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

// ── Control button ─────────────────────────────────────────
function CtrlBtn({ onClick, active, danger, children, title }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36, height: 36,
        borderRadius: '50%',
        border: danger
          ? '1px solid rgba(255,68,68,0.4)'
          : active
            ? '1px solid rgba(232,119,34,0.45)'
            : '1px solid rgba(232,119,34,0.15)',
        background: danger
          ? hover ? 'rgba(255,68,68,0.3)' : 'rgba(255,68,68,0.15)'
          : active
            ? hover ? 'rgba(232,119,34,0.25)' : 'rgba(232,119,34,0.12)'
            : hover ? 'rgba(232,119,34,0.1)' : 'transparent',
        color: danger ? '#FF4444' : active ? ACCENT : 'rgba(255,255,255,0.45)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14,
        transition: 'all 0.15s ease',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

// ── Reaction burst (outside component — uses no state) ────
function burstReaction(emoji, e) {
  for (let i = 0; i < 7; i++) {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'fixed',
      left: (e.clientX + (Math.random() - 0.5) * 80) + 'px',
      top:  (e.clientY + (Math.random() - 0.5) * 40) + 'px',
      fontSize: '22px', pointerEvents: 'none', zIndex: 99999,
      animation: 'lp-burst 1.4s ease forwards',
      animationDelay: (Math.random() * 0.15) + 's',
      '--spin': (Math.random() * 60 - 30) + 'deg',
    });
    el.textContent = emoji;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1800);
  }
}

// ═══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function LaunchpadBubble({
  user,                    // { id, name, email }
  sessionId,               // string — channel / room id
  channelType = 'public',  // 'public' | 'dm'
  channelMembers = [],     // [{ id, name, initials, accent }] — for channel picker
  channelName = '',        // display name for invite notifications
  autoJoin = false,        // if true, join immediately (used for incoming-call auto-accept)
  onAutoJoinComplete,      // callback after autoJoin fires
  notifyUserViaPush,       // optional fn(userId, title, body) — sends push to background devices
}) {
  const [expanded,       setExpanded]       = useState(false);
  const [dragging,       setDragging]       = useState(false);
  const [pos,            setPos]            = useState({ x: 24, y: null });
  const [showPicker,     setShowPicker]     = useState(false);
  const [selected,       setSelected]       = useState(new Set());
  const [panelSize,      setPanelSize]      = useState({ w: 340, h: null });
  const [resizing,       setResizing]       = useState(false);
  const [bubbleScale,    setBubbleScale]    = useState(1);   // multiplier for collapsed bubble size
  const [bubbleResizing, setBubbleResizing] = useState(false);
  const dragOffset       = useRef({ x: 0, y: 0 });
  const resizeStart      = useRef({ x: 0, y: 0, w: 340, h: 0 });
  const bubbleResizeStart = useRef({ y: 0, scale: 1 });
  const panelRef         = useRef(null);
  const bubbleRef        = useRef(null);

  const {
    localStream, peers,
    micOn, camOn, speaking,
    status, join, leave,
    toggleMic, toggleCam,
    isSharing, shareScreen, stopScreenShare,
  } = useLaunchpad({ sessionId, user });

  const isLive = status === 'live';

  // Auto-join (for incoming call accept)
  useEffect(() => {
    if (autoJoin) {
      join();
      onAutoJoinComplete?.();
    }
  }, [autoJoin, join, onAutoJoinComplete]);

  // ── Join handler — broadcasts invites then joins ──────
  const handleJoin = async (inviteUserIds = []) => {
    setShowPicker(false);
    await join();
    if (inviteUserIds.length > 0) {
      await broadcastLaunchpadInvite({
        sessionId,
        channelId:   sessionId,
        channelName: channelName || sessionId,
        fromUser:    user,
        toUserIds:   inviteUserIds,
      });
      // Fire push notifications for users who may be in the background / on mobile
      if (notifyUserViaPush) {
        const title = `📞 Incoming call from ${user.name}`;
        const body  = `Join ${channelName || 'the call'} on 40Geo Nuggets`;
        inviteUserIds.forEach((uid) => notifyUserViaPush(uid, title, body));
      }
    }
  };

  // ── Bubble click handler ──────────────────────────────
  const handleBubbleClick = () => {
    if (dragging) return;
    if (isLive) { setExpanded(true); return; }
    if (channelType === 'dm') {
      // DM: invite the other participant automatically
      const otherId = channelMembers.find(m => m.id !== user?.id)?.id;
      handleJoin(otherId ? [otherId] : []);
    } else {
      // Channel: show participant picker
      setShowPicker(true);
    }
  };

  // ── Drag ───────────────────────────────────────────────
  const onMouseDown = (e) => {
    if (expanded) return;
    setDragging(true);
    const rect = bubbleRef.current.getBoundingClientRect();
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    e.preventDefault();
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    const onUp   = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // ── Panel resize ───────────────────────────────────────
  const onResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current.getBoundingClientRect();
    resizeStart.current = { x: e.clientX, y: e.clientY, w: rect.width, h: rect.height };
    setResizing(true);
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const dx = e.clientX - resizeStart.current.x;
      const dy = e.clientY - resizeStart.current.y;
      setPanelSize({
        w: Math.max(280, resizeStart.current.w + dx),
        h: Math.max(200, resizeStart.current.h + dy),
      });
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [resizing]);

  // ── Bubble resize (collapsed state) ────────────────────
  const onBubbleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    bubbleResizeStart.current = { y: e.clientY, scale: bubbleScale };
    setBubbleResizing(true);
  };

  useEffect(() => {
    if (!bubbleResizing) return;
    const onMove = (e) => {
      const dy = bubbleResizeStart.current.y - e.clientY; // drag up = bigger
      const next = Math.min(3, Math.max(0.6, bubbleResizeStart.current.scale + dy / 80));
      setBubbleScale(next);
    };
    const onUp = () => setBubbleResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [bubbleResizing]);

  const posStyle = expanded
    ? { bottom: 24, right: 24, left: 'auto', top: 'auto' }
    : pos.y !== null
      ? { left: pos.x, top: pos.y, bottom: 'auto', right: 'auto' }
      : { left: pos.x, bottom: 24, top: 'auto', right: 'auto' };

  const allPeers = [
    {
      id: user?.id || 'local',
      name: user?.name || 'You',
      initials: getInitials(user?.name || 'You'),
      color: colorFromId(user?.id || 'local'),
      stream: localStream,
      speaking, micOn, camOn, isLocal: true,
    },
    ...peers,
  ];

  // Members available to invite (exclude self)
  const invitableMembers = channelMembers.filter(m => m.id !== user?.id);

  return (
    <>
      <style>{`
        @keyframes lp-pulse {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:.5; transform:scale(.7); }
        }
        @keyframes lp-ring {
          0%,100% { opacity:.8; transform:scale(1); }
          50%      { opacity:.3; transform:scale(1.08); }
        }
        @keyframes lp-panel-in {
          from { opacity:0; transform:translateY(12px) scale(.97); }
          to   { opacity:1; transform:translateY(0) scale(1); }
        }
        @keyframes lp-burst {
          0%   { opacity:1; transform:translateY(0) scale(1) rotate(0deg); }
          60%  { opacity:1; }
          100% { opacity:0; transform:translateY(-140px) scale(.4) rotate(var(--spin,20deg)); }
        }
        .lp-tooltip { opacity: 0 !important; }
        *:hover > .lp-tooltip { opacity: 1 !important; }
      `}</style>

      {/* ── PARTICIPANT PICKER MODAL ── */}
      {showPicker && !isLive && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
        }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowPicker(false); }}
        >
          <div style={{
            background: 'rgba(13,27,42,0.98)',
            border: '1px solid rgba(232,119,34,0.25)',
            borderRadius: 16,
            padding: '20px 24px',
            minWidth: 300,
            maxWidth: 400,
            boxShadow: '0 24px 80px rgba(0,0,0,0.8)',
            fontFamily: "'Montserrat',sans-serif",
            animation: 'lp-panel-in 0.2s cubic-bezier(0.4,0,0.2,1)',
          }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: ACCENT, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 4 }}>
                🚀 Start Launchpad
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                Select who to invite to this call
              </div>
            </div>

            {invitableMembers.length === 0 ? (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginBottom: 16 }}>
                No other members in this channel yet.
              </div>
            ) : (
              <div style={{ marginBottom: 16, maxHeight: 220, overflowY: 'auto' }}>
                {/* Select all toggle */}
                <div
                  onClick={() => {
                    if (selected.size === invitableMembers.length) {
                      setSelected(new Set());
                    } else {
                      setSelected(new Set(invitableMembers.map(m => m.id)));
                    }
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                    marginBottom: 4,
                    background: 'rgba(232,119,34,0.08)',
                    border: '1px solid rgba(232,119,34,0.15)',
                  }}
                >
                  <div style={{
                    width: 16, height: 16, borderRadius: 4,
                    border: `2px solid ${ACCENT}`,
                    background: selected.size === invitableMembers.length ? ACCENT : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {selected.size === invitableMembers.length && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: ACCENT, letterSpacing: '0.08em' }}>ALL MEMBERS</span>
                </div>

                {invitableMembers.map(m => {
                  const isSelected = selected.has(m.id);
                  return (
                    <div
                      key={m.id}
                      onClick={() => {
                        setSelected(prev => {
                          const next = new Set(prev);
                          isSelected ? next.delete(m.id) : next.add(m.id);
                          return next;
                        });
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', borderRadius: 6, cursor: 'pointer',
                        transition: 'background 0.1s',
                        background: isSelected ? 'rgba(232,119,34,0.1)' : 'transparent',
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 4,
                        border: `2px solid ${isSelected ? ACCENT : 'rgba(255,255,255,0.2)'}`,
                        background: isSelected ? ACCENT : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, transition: 'all 0.15s',
                      }}>
                        {isSelected && (
                          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%',
                        background: m.accent || colorFromId(m.id),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0,
                      }}>
                        {m.initials || getInitials(m.name)}
                      </div>
                      <span style={{ fontSize: 12, color: isSelected ? '#fff' : 'rgba(255,255,255,0.65)', fontWeight: isSelected ? 600 : 400 }}>
                        {m.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowPicker(false)}
                style={{
                  flex: 1, padding: '9px 0',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 8, cursor: 'pointer',
                  color: 'rgba(255,255,255,0.5)',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.1em',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={() => handleJoin([...selected])}
                style={{
                  flex: 2, padding: '9px 0',
                  background: `linear-gradient(135deg, ${ACCENT}, #C4611A)`,
                  border: 'none',
                  borderRadius: 8, cursor: 'pointer',
                  color: '#fff',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800,
                  letterSpacing: '0.12em',
                  boxShadow: `0 4px 16px rgba(232,119,34,0.35)`,
                }}
              >
                🚀 LAUNCH{selected.size > 0 ? ` (${selected.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXPANDED PANEL ── */}
      {expanded && isLive && (
        <div ref={panelRef} style={{
          position: 'fixed', ...posStyle, zIndex: 9999,
          width: panelSize.w,
          height: panelSize.h || undefined,
          display: 'flex', flexDirection: 'column',
          background: 'rgba(13,27,42,0.97)',
          border: '1px solid rgba(232,119,34,0.2)',
          borderRadius: 16,
          backdropFilter: 'blur(24px)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)',
          overflow: 'hidden',
          fontFamily: "'Montserrat',sans-serif",
          animation: 'lp-panel-in 0.25s cubic-bezier(0.4,0,0.2,1)',
          cursor: resizing ? 'nwse-resize' : 'default',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px',
            borderBottom: '1px solid rgba(232,119,34,0.1)',
            background: 'rgba(27,43,91,0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: ACCENT, boxShadow: `0 0 8px ${ACCENT}`,
                animation: 'lp-pulse 2s ease-in-out infinite',
              }} />
              <span style={{
                fontFamily: "'Montserrat',sans-serif", fontWeight: 900,
                fontSize: 11, letterSpacing: '0.25em',
                color: ACCENT, textTransform: 'uppercase',
              }}>
                LAUNCHPAD
              </span>
              <span style={{
                fontSize: 9, color: 'rgba(232,119,34,0.6)',
                background: 'rgba(232,119,34,0.1)',
                border: '1px solid rgba(232,119,34,0.25)',
                borderRadius: 100, padding: '1px 7px',
                letterSpacing: '0.08em', fontWeight: 700,
              }}>
                {allPeers.length} LIVE
              </span>
            </div>
            <button
              onClick={() => setExpanded(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.35)', fontSize: 16, lineHeight: 1,
                padding: 4, borderRadius: 4,
              }}
            >✕</button>
          </div>

          {/* Video grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: allPeers.length === 1 ? '1fr' : allPeers.length <= 4 ? '1fr 1fr' : '1fr 1fr 1fr',
            gap: 6, padding: 10,
            flex: 1, overflowY: 'auto',
          }}>
            {allPeers.map(p => (
              <VideoTile
                key={p.id}
                stream={p.stream}
                name={p.isLocal ? 'You' : p.name}
                initials={p.initials}
                color={p.color}
                speaking={p.speaking}
                muted={p.isLocal}
                size={allPeers.length === 1 ? 'lg' : 'md'}
              />
            ))}
          </div>

          {/* Controls */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, padding: '8px 14px 12px',
            borderTop: '1px solid rgba(232,119,34,0.1)',
          }}>
            <CtrlBtn onClick={toggleMic} active={micOn} title={micOn ? 'Mute' : 'Unmute'}>
              {micOn
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2a3 3 0 0 1 3 3v7a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
              }
            </CtrlBtn>

            <CtrlBtn onClick={toggleCam} active={camOn} title={camOn ? 'Camera off' : 'Camera on'}>
              {camOn
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
                : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34m-7.72-2.06a4 4 0 1 1-5.56-5.56"/></svg>
              }
            </CtrlBtn>

            {/* Screen share */}
            <CtrlBtn
              onClick={isSharing ? stopScreenShare : shareScreen}
              active={isSharing}
              title={isSharing ? 'Stop sharing' : 'Share screen'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
                {isSharing && <line x1="1" y1="1" x2="23" y2="23"/>}
              </svg>
            </CtrlBtn>

            {['🚀','⚡','🔥'].map(e => (
              <CtrlBtn key={e} active onClick={(ev) => burstReaction(e, ev)} title="React">
                <span style={{ fontSize: 14 }}>{e}</span>
              </CtrlBtn>
            ))}

            <CtrlBtn onClick={leave} danger title="Leave">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </CtrlBtn>
          </div>

          {/* Resize handle */}
          <div
            onMouseDown={onResizeMouseDown}
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 18, height: 18,
              cursor: 'nwse-resize',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end',
              padding: 4,
            }}
          >
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
              <path d="M7 1L1 7M7 4L4 7M7 7" stroke="rgba(232,119,34,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
      )}

      {/* ── COLLAPSED BUBBLE ── */}
      {!(expanded && isLive) && (
        <div
          ref={bubbleRef}
          onMouseDown={onMouseDown}
          onClick={handleBubbleClick}
          style={{
            position: 'fixed', ...posStyle, zIndex: 9999,
            cursor: dragging ? 'grabbing' : 'pointer',
            userSelect: 'none',
            transform: `scale(${bubbleScale})`,
            transformOrigin: 'bottom left',
          }}
        >
          <div style={{
            position: 'relative',
            width: isLive ? 'auto' : 52,
            height: 52,
            display: 'flex', alignItems: 'center', gap: 0,
          }}>
            {/* Stacked avatars when live */}
            {isLive && allPeers.slice(0, 4).map((p, i) => (
              <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -16, zIndex: 4 - i, position: 'relative' }}>
                <VideoTile {...p} muted={p.isLocal} size="sm" />
              </div>
            ))}

            {/* Overflow count */}
            {isLive && allPeers.length > 4 && (
              <div style={{
                marginLeft: -16, zIndex: 0,
                width: 56, height: 56, borderRadius: '50%',
                background: NAVY,
                border: '2px solid rgba(232,119,34,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: ACCENT,
                fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
              }}>
                +{allPeers.length - 4}
              </div>
            )}

            {/* Idle button */}
            {!isLive && (
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(232,119,34,0.18), rgba(27,43,91,0.4))',
                border: '1.5px solid rgba(232,119,34,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
                boxShadow: '0 0 20px rgba(232,119,34,0.18), 0 4px 20px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(12px)',
              }}>
                🚀
              </div>
            )}

            {/* Live dot */}
            {isLive && (
              <div style={{
                position: 'absolute', top: 2, right: -2,
                width: 10, height: 10, borderRadius: '50%',
                background: ACCENT, boxShadow: `0 0 8px ${ACCENT}`,
                border: `2px solid ${NAVY}`,
                animation: 'lp-pulse 2s ease-in-out infinite',
              }} />
            )}
          </div>

          {/* Live mini controls bar — mic / cam / expand / hang-up */}
          {isLive && (
            <div
              onClick={e => e.stopPropagation()}
              style={{
                marginTop: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                background: 'rgba(13,27,42,0.92)',
                border: '1px solid rgba(232,119,34,0.2)',
                borderRadius: 20,
                padding: '4px 8px',
                backdropFilter: 'blur(12px)',
              }}
            >
              {/* Mic */}
              <button onClick={toggleMic} title={micOn ? 'Mute' : 'Unmute'} style={{
                width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: micOn ? 'rgba(232,119,34,0.15)' : 'rgba(255,68,68,0.2)',
                color: micOn ? ACCENT : '#FF4444',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              }}>
                {micOn ? '🎙' : '🔇'}
              </button>

              {/* Cam */}
              <button onClick={toggleCam} title={camOn ? 'Camera off' : 'Camera on'} style={{
                width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: camOn ? 'rgba(232,119,34,0.15)' : 'rgba(255,68,68,0.2)',
                color: camOn ? ACCENT : '#FF4444',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              }}>
                {camOn ? '📹' : '🚫'}
              </button>

              {/* Expand */}
              <button onClick={() => setExpanded(true)} title="Expand" style={{
                width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              }}>
                ⛶
              </button>

              {/* Hang up */}
              <button onClick={() => { leave(); }} title="Hang up" style={{
                width: 26, height: 26, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: 'rgba(255,68,68,0.2)', color: '#FF4444',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11,
              }}>
                ✕
              </button>
            </div>
          )}

          {/* Bubble resize handle (drag up = bigger, drag down = smaller) */}
          <div
            onMouseDown={onBubbleResizeMouseDown}
            onClick={e => e.stopPropagation()}
            title="Drag to resize bubble"
            style={{
              position: 'absolute', bottom: isLive ? -4 : -8, right: -8,
              width: 14, height: 14, borderRadius: '50%',
              background: 'rgba(232,119,34,0.5)', border: '1px solid rgba(232,119,34,0.8)',
              cursor: 'nwse-resize',
            }}
          />

          {/* Tooltip */}
          {!isLive && (
            <div className="lp-tooltip" style={{
              position: 'absolute', bottom: '110%', left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(13,27,42,0.95)',
              border: '1px solid rgba(232,119,34,0.2)',
              borderRadius: 6, padding: '4px 10px',
              fontSize: 10, color: ACCENT,
              fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
              letterSpacing: '0.12em',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}>
              START LAUNCHPAD
            </div>
          )}
        </div>
      )}
    </>
  );
}
